/** Dedicated, deep-linkable tenant detail page (WSRPDM-0021 U2). */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { usePolling } from '@/hooks/usePolling';
import { jobTypeLabel } from '@/domain/registry';
import { AppSwitch, KpiCard, StatusPill } from '@/components/ui';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { getTenant as getIdentityTenant } from '@/api/saas/tenants';
import { getTenant, runNow, setServiceEnabled, setTenantEnabled } from '@/api/wsr/tenants';
import { getJobs, getUpcoming } from '@/api/wsr/queue';
import { ApiError } from '@/api/errors';
import type { TenantDto } from '@/api/saas/types';
import type {
  GenesisPhase,
  GenesisProgressView,
  JobType,
  LastRunView,
  NextRunView,
  PlatformTenantDetail,
  PlatformTenantSummary,
  RunNowBlocked,
  ServiceEnableView,
  TenantProblem,
  TenantWarehouse,
  UpcomingRun,
  WarehouseDataset,
} from '@/api/wsr/types';
import { ConfigForm } from './ConfigForm';
import { TenantIdentityHeader } from './TenantIdentityHeader';
import { MembersPanel } from './MembersPanel';
import { InvitationsPanel } from './InvitationsPanel';
import { WStudioSettings } from './settings/WStudioSettings';
import { progressEntries } from './genesisProgress';
import { formatSecondsDuration } from '../queue/duration';
import { ActiveJobsTable, UpcomingTable } from '../queue/QueueTables';
import { compareQueueJobs } from '../queue/tableValues';
import { ActivityCountersLine, activityKindValue } from './activityFormat';
import { SyncHistoryPanel, parseJobId } from './history/SyncHistoryPanel';

const TAB_KEYS = [
  'overview',
  'members',
  'invitations',
  'ingestion',
  'history',
  'wstudio',
  'contacts',
  'advanced',
] as const;
type TabKey = (typeof TAB_KEYS)[number];
type LoadError = 'not-found' | 'generic' | null;

const CONFIG_TAB_KEYS = ['wstudio', 'contacts', 'advanced'] as const;
type ConfigTabKey = (typeof CONFIG_TAB_KEYS)[number];

function isConfigTab(value: string): value is ConfigTabKey {
  return (CONFIG_TAB_KEYS as readonly string[]).includes(value);
}

function resolveTab(value: string | null): TabKey {
  if (value === 'settings') return 'wstudio';
  if (value !== null && (TAB_KEYS as readonly string[]).includes(value)) {
    return value as TabKey;
  }
  return 'overview';
}

const RUN_NOW_BLOCKED_COPY: Record<string, { key: string; fallback: string }> = {
  GLOBAL_DISABLED: {
    key: 'runNow.blocked.GLOBAL_DISABLED',
    fallback: 'Ingestion is globally disabled.',
  },
  TENANT_DISABLED: {
    key: 'runNow.blocked.TENANT_DISABLED',
    fallback: 'This tenant is disabled.',
  },
  SERVICE_DISABLED: {
    key: 'runNow.blocked.SERVICE_DISABLED',
    fallback: 'This service is switched off.',
  },
  NOT_VERIFIED: {
    key: 'runNow.blocked.NOT_VERIFIED',
    fallback: 'Run the dry-run tests first.',
  },
  NOT_SYNC_READY: {
    key: 'runNow.blocked.NOT_SYNC_READY',
    fallback: 'Waiting for history to finish loading.',
  },
  NOT_CONFIGURED: {
    key: 'runNow.blocked.NOT_CONFIGURED',
    fallback: 'WStudio credentials are not configured for this tenant.',
  },
};

function runNowBlockedReason(reason: string, t: TFn): string {
  const copy = Object.prototype.hasOwnProperty.call(RUN_NOW_BLOCKED_COPY, reason)
    ? RUN_NOW_BLOCKED_COPY[reason]
    : undefined;
  return copy
    ? t(copy.key, copy.fallback)
    : t('runNow.blocked.unknown', 'This service cannot run right now.');
}

/** Explanation only. The button's actual truth claim reads `service.effective`. */
function runNowTooltip(summary: PlatformTenantSummary, service: ServiceEnableView, t: TFn): string {
  if (service.effective === undefined) return runNowBlockedReason('unknown', t);
  if (!summary.globalEnabled) return runNowBlockedReason('GLOBAL_DISABLED', t);
  if (!summary.tenantEnabled) return runNowBlockedReason('TENANT_DISABLED', t);
  if (!service.enabled) return runNowBlockedReason('SERVICE_DISABLED', t);
  if (!summary.wstudioConfigured) return runNowBlockedReason('NOT_CONFIGURED', t);
  return runNowBlockedReason('unknown', t);
}

/** A 409 body is preserved verbatim by client.ts in ApiError.details.raw. */
function runNowBlockedBy(error: unknown): string[] | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const raw = error.details.raw as Partial<RunNowBlocked> | null;
  if (!raw || !Array.isArray(raw.blockedBy)) return [];
  return raw.blockedBy.filter((reason): reason is string => typeof reason === 'string');
}

/**
 * Action banners only — NOT_CONFIGURED / NOT_VERIFIED. Loading phases
 * (`GENESIS_SALES`, `GENESIS_ATTENDANCE`, `SYNCING`) and `COMPLETE` render
 * nothing; genesis status lives on Sync history as Kind. `READY_TO_START` is
 * resolved separately because three independent switches sit behind it.
 *
 * Do not read `actionRequired` here: the phase already determines the banner,
 * and using both would reintroduce two sources of truth for one decision.
 */
const READINESS_COPY: Record<
  Exclude<
    GenesisPhase,
    'COMPLETE' | 'READY_TO_START' | 'GENESIS_SALES' | 'GENESIS_ATTENDANCE' | 'SYNCING'
  >,
  { severity: 'warning'; key: string; fallback: string }
> = {
  NOT_CONFIGURED: {
    severity: 'warning',
    key: 'readiness.notConfigured',
    fallback: 'WStudio is not configured for this tenant yet.',
  },
  NOT_VERIFIED: {
    severity: 'warning',
    key: 'readiness.notVerified',
    fallback:
      'Run the WStudio dry-run tests to verify this configuration. If the configuration changed recently, re-run them.',
  },
};

/**
 * `READY_TO_START` means "verified, but the switches are off" — and there are three
 * independent switches behind that one phase: the platform-wide `globalEnabled`, the
 * tenant's `tenantEnabled`, and each `services[].enabled`. Naming only the tenant
 * switch sent an operator to the wrong page when the real blocker was the
 * platform-wide Global switch — the production failure this spec fixes (tenant
 * overview redesign handoff §5). Evaluated in the order an operator must fix them:
 * outermost switch first.
 */
function readyToStartCopy(
  summary: PlatformTenantDetail['summary'],
  t: ReturnType<typeof useCopy>['t'],
): string {
  if (summary.globalEnabled === false) {
    return t(
      'readiness.blockedGlobal',
      'Ingestion is disabled platform-wide. Enable it under Tenants → Enable global ingestion.',
    );
  }
  if (summary.tenantEnabled === false) {
    return t(
      'readiness.blockedTenant',
      'Verified and ready. Enable this tenant to start loading its history.',
    );
  }
  const disabledServices = summary.services.filter((service) => !service.enabled);
  if (disabledServices.length > 0) {
    return t(
      'readiness.blockedServices',
      'Verified and ready, but these services are off: {{services}}.',
      { services: disabledServices.map((service) => jobTypeLabel(service.jobType, t)).join(', ') },
    );
  }
  // `READY_TO_START` with all three switches on is not expected — the server would
  // not emit it — but a lookup that can fall through to nothing would blank the
  // banner on an unanticipated payload. Fail closed, same spirit as the unknown-phase
  // guard below: keep the original generic string as the fallback.
  return t(
    'readiness.readyToStart',
    'Verified and ready. Enable this tenant to start loading its history.',
  );
}

function ReadinessAlert({ summary }: { summary: PlatformTenantDetail['summary'] }) {
  const { t } = useCopy();

  const phase = summary.genesisPhase;
  if (
    phase === undefined ||
    phase === 'COMPLETE' ||
    phase === 'GENESIS_SALES' ||
    phase === 'GENESIS_ATTENDANCE' ||
    phase === 'SYNCING'
  ) {
    return null;
  }

  if (phase === 'READY_TO_START') {
    return <Alert severity="warning">{readyToStartCopy(summary, t)}</Alert>;
  }

  // `phase` is server JSON, not a runtime-validated value — a future phase this
  // console doesn't know about yet must not crash the page. Fail closed.
  const copy = READINESS_COPY[phase];
  if (!copy) return null;

  return <Alert severity={copy.severity}>{t(copy.key, copy.fallback)}</Alert>;
}

/**
 * Tenant overview redesign handoff §3 — "state and data resident, configuration
 * reachable." Replaces the old eight-row enable card: all-on renders nothing (the
 * status line above already says so), and anything off becomes the loudest thing
 * on the page instead of one identical row among eight. Read-only — the Ingestion
 * tab remains the only place a switch is actually changed, hence the closing hint.
 */
function DisabledSwitchesSection({ summary }: { summary: PlatformTenantDetail['summary'] }) {
  const { t } = useCopy();

  const offRows: { key: string; label: string }[] = [];
  if (summary.globalEnabled === false) {
    offRows.push({
      key: 'global',
      label: t('tenantDetail.overview.disabledGlobal', 'Ingestion is off platform-wide'),
    });
  }
  if (summary.tenantEnabled === false) {
    offRows.push({
      key: 'tenant',
      label: t('tenantDetail.overview.disabledTenant', 'This tenant is off'),
    });
  }
  summary.services
    .filter((service) => !service.enabled)
    .forEach((service) => {
      offRows.push({ key: service.jobType, label: jobTypeLabel(service.jobType, t) });
    });

  if (offRows.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2">
        {t('tenantDetail.overview.disabledTitle', 'Disabled')}
      </Typography>
      <List dense disablePadding sx={{ mt: 1 }}>
        {offRows.map((row) => (
          <ListItem key={row.key} disableGutters>
            <ListItemText primary={row.label} />
          </ListItem>
        ))}
      </List>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t('tenantDetail.overview.disabledHint', 'Change these on the Ingestion tab.')}
      </Typography>
    </Paper>
  );
}

/**
 * WSRPDM-0045's verification-derived rows survive unchanged as the explicit
 * pre-WSRAPI-0083 fallback. Rows stay in `verification.jobs[]` order — do not
 * invent a severity ranking between stale and failed.
 */
function buildVerificationProblemRows(
  verification: PlatformTenantDetail['summary']['verification'],
  t: ReturnType<typeof useCopy>['t'],
): { key: string; text: string }[] {
  if (!verification) return [];
  const rows: { key: string; text: string }[] = [];
  verification.jobs.forEach((job) => {
    const label = jobTypeLabel(job.jobType, t);
    if (job.stale) {
      rows.push({
        key: `${job.jobType}-stale`,
        text: t(
          'tenantDetail.overview.problemStale',
          '{{job}} — verification is stale, re-run the dry run',
          { job: label },
        ),
      });
    }
    if (job.status === 'FAILED' || job.status === 'NOT_CONFIGURED') {
      rows.push({
        key: `${job.jobType}-failed`,
        text: t('tenantDetail.overview.problemFailed', '{{job}} — verification failed', {
          job: label,
        }),
      });
    }
  });
  return rows;
}

/**
 * WSRAPI-0083 §5 / WSRAPI-0090 §2e — failures outrank flags, which outrank
 * verification issues, while rows within each kind retain server order. Unknown kinds and malformed details
 * fail closed to the generic truth supplied by the server: this tenant needs
 * attention, without inventing unavailable detail.
 */
function buildServerProblemRows(
  problems: TenantProblem[],
  t: ReturnType<typeof useCopy>['t'],
): { key: string; text: string }[] {
  const ordered = [
    ...problems.filter((problem) => problem.kind === 'JOB_FAILURES'),
    ...problems.filter((problem) => problem.kind === 'GHL_WRITE_FAILURES'),
    ...problems.filter((problem) => problem.kind === 'NO_DATA'),
    ...problems.filter((problem) => problem.kind === 'VERIFICATION'),
    ...problems.filter(
      (problem) =>
        problem.kind !== 'JOB_FAILURES' &&
        problem.kind !== 'GHL_WRITE_FAILURES' &&
        problem.kind !== 'NO_DATA' &&
        problem.kind !== 'VERIFICATION',
    ),
  ];

  return ordered.map((problem, index) => {
    const job = jobTypeLabel(problem.jobType, t);
    const generic = () =>
      t('problems.generic', '{{job}} — needs attention', {
        job,
      });

    if (problem.kind === 'VERIFICATION') {
      return {
        key: `${problem.kind}-${problem.jobType}-${index}`,
        text: t(
          'problems.verification',
          '{{job}} — verification is not passing; re-run the dry run',
          { job },
        ),
      };
    }

    if (problem.kind === 'JOB_FAILURES') {
      const occurredAt = problem.occurredAt === null ? Number.NaN : Date.parse(problem.occurredAt);
      if (problem.errorCode === null || problem.count === null || !Number.isFinite(occurredAt)) {
        return { key: `${problem.kind}-${problem.jobType}-${index}`, text: generic() };
      }
      const ageSeconds = Math.max(0, Math.floor((Date.now() - occurredAt) / 1000));
      return {
        key: `${problem.kind}-${problem.jobType}-${index}`,
        text: t('problems.jobFailures', '{{job}} — {{code}}, {{count}}× in 24h, last {{ago}} ago', {
          job,
          code: problem.errorCode,
          count: problem.count,
          ago: formatSecondsDuration(ageSeconds),
        }),
      };
    }

    if (problem.kind === 'GHL_WRITE_FAILURES') {
      if (problem.count === null) {
        return { key: `${problem.kind}-${problem.jobType}-${index}`, text: generic() };
      }
      return {
        key: `${problem.kind}-${problem.jobType}-${index}`,
        text: t(
          'problems.ghlWriteFailures',
          '{{count}} contacts failed to write to GoHighLevel on the last run.',
          { count: problem.count },
        ),
      };
    }

    if (problem.kind === 'NO_DATA') {
      return {
        key: `${problem.kind}-${problem.jobType}-${index}`,
        text: t('problems.noData', '{{job}} — no records on the last run; worth a look.', { job }),
      };
    }

    return { key: `unknown-${problem.jobType}-${index}`, text: generic() };
  });
}

type TFn = ReturnType<typeof useCopy>['t'];

/** Row count, locale thousands separators; em-dash when the count query failed
 *  (WSRAPI-0078 fails safe — `rows: null` is not an error). */
function warehouseRowsValue(rows: number | null): string {
  return rows === null ? '—' : rows.toLocaleString();
}

/**
 * Freshness subline — `syncedAt` means "we last looked", never "the data
 * changed" (see WSRPDM-0046 spec: diff-on-write writes nothing on an
 * unchanged row, so an `updatedAt` would not advance the way this does).
 * `syncedAt: null` renders no subline at all — no "never", no em-dash; the
 * count alone is the honest rendering.
 */
function warehouseFreshness(dataset: WarehouseDataset, t: TFn): string | undefined {
  if (dataset.syncedAt === null) return undefined;
  const elapsedSeconds = Math.max(
    0,
    Math.floor((Date.now() - new Date(dataset.syncedAt).getTime()) / 1000),
  );
  return t('warehouse.syncedAgo', 'synced {{duration}} ago', {
    duration: formatSecondsDuration(elapsedSeconds),
  });
}

/** yyyy-MM-dd → locale-formatted "MMM yyyy", parsed as local calendar date
 *  components (not `new Date(str)`) so no timezone can shift the day across
 *  a month/year boundary. */
function formatMonthYear(dateOnly: string, locale: string): string {
  const year = Number(dateOnly.slice(0, 4));
  const month = Number(dateOnly.slice(5, 7));
  const day = Number(dateOnly.slice(8, 10));
  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    month: 'short',
    year: 'numeric',
  });
}

/** Sales: full history once `SALES_SYNC`'s genesis entry is complete, else freshness. */
function salesSubline(
  dataset: WarehouseDataset,
  genesis: GenesisProgressView[],
  t: TFn,
): string | undefined {
  const entry = genesis.find((e) => e.jobType === 'SALES_SYNC');
  if (entry?.genesisComplete === true) return t('warehouse.fullHistory', 'full history');
  return warehouseFreshness(dataset, t);
}

/**
 * Attendance: depth ("back to {{date}}") while backfilling — "the number
 * that shows the walk moving" (handoff §6) — full history once
 * `ATTENDANCE_SYNC`'s genesis entry completes. Falls back to freshness when
 * `coveredFrom` is null or the entry is absent, and to nothing if that too
 * is unavailable.
 */
function attendanceSubline(
  dataset: WarehouseDataset,
  genesis: GenesisProgressView[],
  t: TFn,
  locale: string,
): string | undefined {
  const entry = genesis.find((e) => e.jobType === 'ATTENDANCE_SYNC');
  if (entry?.genesisComplete === true) return t('warehouse.fullHistory', 'full history');
  if (entry?.coveredFrom != null) {
    return t('warehouse.backTo', 'back to {{date}}', {
      date: formatMonthYear(entry.coveredFrom, locale),
    });
  }
  return warehouseFreshness(dataset, t);
}

/**
 * WSRAPI-0078 / handoff §6 — four warehouse cards below the status line,
 * above Problems. Renders nothing at all while `warehouse` is undefined
 * (pre-deploy) — no placeholder cards, zeros, or skeletons for the gap.
 * Reuses `KpiCard` (no new card primitive) and the queue screens'
 * `formatSecondsDuration` (no second duration formatter).
 */
function WarehouseSection({
  summary,
  warehouse,
}: {
  summary: PlatformTenantSummary;
  warehouse: TenantWarehouse | undefined;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();

  if (!warehouse) return null;

  const genesis = progressEntries(summary);
  // `KpiCardProps.hint` is `hint?: string` — under `exactOptionalPropertyTypes`
  // that forbids an explicit `undefined`, so the "no subline" case (spec: no
  // subline at all when `syncedAt` is null) is expressed by omitting the prop
  // via spread rather than passing `hint={undefined}`.
  const membersHint = warehouseFreshness(warehouse.members, t);
  const salesHint = salesSubline(warehouse.sales, genesis, t);
  const attendanceHint = attendanceSubline(warehouse.attendance, genesis, t, i18n.language);
  const productsHint = warehouseFreshness(warehouse.products, t);

  return (
    <Box>
      <Typography id="warehouse-heading" variant="subtitle2" sx={{ mb: 1 }}>
        {t('warehouse.title', 'Warehouse')}
      </Typography>
      <Grid container spacing={2} role="group" aria-labelledby="warehouse-heading">
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t('warehouse.members', 'WStudio members')}
            value={warehouseRowsValue(warehouse.members.rows)}
            valueVariant="h1"
            {...(membersHint !== undefined ? { hint: membersHint } : {})}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t('warehouse.sales', 'Sales')}
            value={warehouseRowsValue(warehouse.sales.rows)}
            valueVariant="h1"
            {...(salesHint !== undefined ? { hint: salesHint } : {})}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t('warehouse.attendance', 'Attendance')}
            value={warehouseRowsValue(warehouse.attendance.rows)}
            valueVariant="h1"
            {...(attendanceHint !== undefined ? { hint: attendanceHint } : {})}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t('warehouse.products', 'Products')}
            value={warehouseRowsValue(warehouse.products.rows)}
            valueVariant="h1"
            {...(productsHint !== undefined ? { hint: productsHint } : {})}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

/** Rows is a COUNT, never a fraction — locale-formatted, em-dash when null/absent. */
function activityRowsValue(rows: number | null | undefined): string {
  return rows == null ? '—' : rows.toLocaleString();
}

/** Relative duration since `finishedAt`, reusing the queue screens' formatter —
 *  never a second duration format (spec §4). Em-dash when there is no last run. */
function activityRanValue(finishedAt: string | undefined, t: TFn): string {
  if (finishedAt === undefined) return '—';
  const elapsedSeconds = Math.max(
    0,
    Math.floor((Date.now() - new Date(finishedAt).getTime()) / 1000),
  );
  return t('activity.ranAgo', '{{duration}} ago', {
    duration: formatSecondsDuration(elapsedSeconds),
  });
}

/**
 * The 24h switch that makes a daily job read as a clock time ("03:00") rather than
 * "in 23h" — a formatting choice, not a contract (spec §4), kept as one named constant.
 */
const ACTIVITY_NEXT_HORIZON_HOURS = 24;

function activityNextValue(nextRun: NextRunView | undefined, t: TFn, locale: string): string {
  if (nextRun === undefined) return '—';
  if (!nextRun.enabled) return t('activity.next.off', 'off');
  if (nextRun.nextDueAt === null) return '—';
  const dueAt = new Date(nextRun.nextDueAt);
  const secondsUntil = Math.floor((dueAt.getTime() - Date.now()) / 1000);
  if (secondsUntil < ACTIVITY_NEXT_HORIZON_HOURS * 3600) {
    return t('activity.next.in', 'in {{duration}}', {
      duration: formatSecondsDuration(secondsUntil),
    });
  }
  return dueAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

interface ActivityRow {
  jobType: JobType;
  lastRun: LastRunView | undefined;
  nextRun: NextRunView | undefined;
}

/**
 * Handoff §7 — the row set starts from `summary.services`, never from
 * `lastRuns`/`nextRuns` directly, then omits a service when its `lastRuns` join misses.
 * A never-run job type is absent because four em-dashes read as "broken" while absence
 * reads as "not applicable". `nextRuns` can carry a null `nextDueAt`, so it is not a
 * complete row source. Kept in `services[]` order — sorting by recency would make the
 * table jump between polls.
 */
function buildActivityRows(
  services: PlatformTenantSummary['services'],
  lastRuns: LastRunView[],
  nextRuns: NextRunView[] | undefined,
): ActivityRow[] {
  return services
    .map((service) => ({
      jobType: service.jobType,
      lastRun: lastRuns.find((run) => run.jobType === service.jobType),
      nextRun: nextRuns?.find((run) => run.jobType === service.jobType),
    }))
    .filter((row) => row.lastRun !== undefined);
}

/**
 * Handoff §7 — the Activity table: one row per registered job type with a completed
 * run, showing what it did (Kind/Rows/Ran) and when it next runs (Next). Gated on `lastRuns`
 * (WSRAPI-0080) — renders NO section at all while it is undefined, even when `nextRuns`
 * (WSRAPI-0077) is present: three of five columns empty is worse than no table, and a
 * Job/Next-only table would falsely read as "nothing has ever run." Neither field is
 * deployed yet, so this absent branch is the live behaviour today.
 */
function ActivitySection({
  summary,
  lastRuns,
  nextRuns,
}: {
  summary: PlatformTenantSummary;
  lastRuns: LastRunView[] | undefined;
  nextRuns: NextRunView[] | undefined;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();

  if (lastRuns === undefined) return null;

  const rows = buildActivityRows(summary.services, lastRuns, nextRuns);

  return (
    <Box>
      <Typography id="activity-heading" variant="subtitle2" sx={{ mb: 1 }}>
        {t('activity.title', 'Activity')}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-labelledby="activity-heading">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{t('activity.col.job', 'Job')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('activity.col.kind', 'Kind')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('activity.col.records', 'Records')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('activity.col.ran', 'Ran')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('activity.col.next', 'Next')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.jobType}>
                <TableCell>
                  {row.lastRun ? (
                    <Link
                      component={RouterLink}
                      to={{ search: `tab=history&job=${row.lastRun.id}` }}
                    >
                      {jobTypeLabel(row.jobType, t)}
                    </Link>
                  ) : (
                    jobTypeLabel(row.jobType, t)
                  )}
                </TableCell>
                <TableCell>{activityKindValue(row.lastRun?.runKind, t)}</TableCell>
                <TableCell>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {activityRowsValue(row.lastRun?.rows)}
                      {row.lastRun?.caughtUp === true ? (
                        <Chip
                          label={t('activity.caughtUp', 'Caught up')}
                          color="default"
                          variant="outlined"
                          size="small"
                        />
                      ) : row.lastRun && row.lastRun.status !== 'SUCCEEDED' ? (
                        <StatusPill status={row.lastRun.status} />
                      ) : null}
                    </Box>
                    <ActivityCountersLine counters={row.lastRun?.counters} />
                  </Box>
                </TableCell>
                <TableCell>{activityRanValue(row.lastRun?.finishedAt, t)}</TableCell>
                <TableCell>{activityNextValue(row.nextRun, t, i18n.language)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/**
 * Handoff §5 — a single quiet line when the authoritative array is empty,
 * never a card or an "All good!" celebration. Before WSRAPI-0083, an absent
 * `problems` field keeps WSRPDM-0045's verification-derived rendering.
 */
function ProblemsSection({
  problems,
  verification,
}: {
  problems: PlatformTenantDetail['problems'];
  verification: PlatformTenantDetail['summary']['verification'];
}) {
  const { t } = useCopy();
  const rows =
    problems === undefined
      ? buildVerificationProblemRows(verification, t)
      : buildServerProblemRows(problems, t);

  return (
    <Box>
      <Typography variant="subtitle2">
        {t('tenantDetail.overview.problemsTitle', 'Problems')}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t(
            'tenantDetail.overview.problemsNone',
            'No problems — failures and stale verification appear here.',
          )}
        </Typography>
      ) : (
        <List dense disablePadding sx={{ mt: 1 }}>
          {rows.map((row) => (
            <ListItem key={row.key} disableGutters>
              <ListItemText primary={row.text} />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

const TENANT_QUEUE_INTERVAL_MS = 10_000;
const UPCOMING_RELATIVE_HORIZON_SECONDS = 24 * 60 * 60;

function tenantUpcomingNextValue(run: UpcomingRun, t: TFn, locale: string): string {
  const dueAt = new Date(run.nextDueAt);
  const secondsUntil = Math.floor((dueAt.getTime() - Date.now()) / 1000);
  if (secondsUntil <= 0) return t('queue.upcoming.dueNow', 'due now');
  if (secondsUntil < UPCOMING_RELATIVE_HORIZON_SECONDS) {
    return t('queue.upcoming.in', 'in {{duration}}', {
      duration: formatSecondsDuration(secondsUntil),
    });
  }
  return dueAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

async function getUpcomingSnapshot() {
  const data = await getUpcoming();
  return { data, nowMs: Date.now() };
}

/**
 * Tenant-scoped view over the platform Queue endpoints. This component exists only while
 * Overview is mounted, so both queue polls and the detail-refresh interval are torn down
 * before Settings can render editable forms. The detail request deliberately starts on the
 * first 10s tick: the parent already owns the initial fetch, avoiding a duplicate response
 * racing the initial page load.
 */
function TenantQueueSection({
  tenantSlug,
  onDetailChanged,
}: {
  tenantSlug: string;
  onDetailChanged: (detail: PlatformTenantDetail) => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const jobsPolling = usePolling(getJobs, {
    intervalMs: TENANT_QUEUE_INTERVAL_MS,
    pauseWhenHidden: true,
  });
  const upcomingPolling = usePolling(getUpcomingSnapshot, {
    intervalMs: TENANT_QUEUE_INTERVAL_MS,
    pauseWhenHidden: true,
  });
  const skipInitialDetailFetch = useRef(true);
  const detailPolling = usePolling(
    () => {
      if (skipInitialDetailFetch.current) {
        skipInitialDetailFetch.current = false;
        return Promise.resolve<PlatformTenantDetail | null>(null);
      }
      return getTenant(tenantSlug);
    },
    { intervalMs: TENANT_QUEUE_INTERVAL_MS, pauseWhenHidden: true },
  );

  useEffect(() => {
    if (detailPolling.data) onDetailChanged(detailPolling.data);
  }, [detailPolling.data, onDetailChanged]);

  // A queue block is a claim about both current and forecast work. Match the Queue page's
  // success gate: until each endpoint has returned data, render no block and no extra error.
  if (!jobsPolling.data || !upcomingPolling.data) return null;

  const jobs = jobsPolling.data.jobs
    .filter((job) => job.tenantSlug === tenantSlug)
    .sort(compareQueueJobs);
  const runs = upcomingPolling.data.data.upcoming.filter((run) => run.tenantSlug === tenantSlug);
  const overdueRuns = runs.filter((run) => run.overdue === true);
  const upcomingRuns = runs.filter((run) => run.overdue !== true);
  const empty = jobs.length === 0 && runs.length === 0;
  const nowMs = upcomingPolling.data.nowMs;

  return (
    <Box component="section" aria-labelledby="tenant-queue-heading">
      <Typography id="tenant-queue-heading" variant="h2" component="h2" gutterBottom>
        {t('tenantQueue.title', 'Queue')}
      </Typography>

      {empty ? (
        <Typography variant="body2" color="text.secondary">
          {t('tenantQueue.empty', 'Nothing running or scheduled.')}
        </Typography>
      ) : (
        <Stack spacing={3}>
          {jobs.length > 0 ? (
            <ActiveJobsTable
              rows={jobs}
              generatedAt={jobsPolling.data.generatedAt}
              emptyMessage={t('queue.empty', 'No active jobs.')}
              locale={i18n.language}
              t={t}
              showTenantColumn={false}
            />
          ) : null}
          {overdueRuns.length > 0 ? (
            <Box>
              <Typography id="tenant-overdue-heading" variant="h3" component="h3" gutterBottom>
                {t('queue.upcoming.overdueTitle', 'Overdue')}
              </Typography>
              <UpcomingTable
                rows={overdueRuns}
                headingId="tenant-overdue-heading"
                nextValue={(run) =>
                  t('queue.upcoming.overdueBy', '{{duration}} overdue', {
                    duration: formatSecondsDuration(
                      Math.floor((nowMs - new Date(run.nextDueAt).getTime()) / 1000),
                    ),
                  })
                }
                t={t}
                showTenantColumn={false}
              />
            </Box>
          ) : null}
          {upcomingRuns.length > 0 ? (
            <Box>
              <Typography id="tenant-upcoming-heading" variant="h3" component="h3" gutterBottom>
                {t('queue.upcoming.title', 'Upcoming')}
              </Typography>
              <UpcomingTable
                rows={upcomingRuns}
                headingId="tenant-upcoming-heading"
                nextValue={(run) => tenantUpcomingNextValue(run, t, i18n.language)}
                t={t}
                showTenantColumn={false}
              />
            </Box>
          ) : null}
        </Stack>
      )}
    </Box>
  );
}

function OverviewPanel({
  tenant,
  ops,
  opsError,
  onDetailChanged,
}: {
  tenant: TenantDto;
  ops: PlatformTenantDetail | null;
  opsError: LoadError;
  onDetailChanged: (detail: PlatformTenantDetail) => void;
}) {
  const { t } = useCopy();

  return (
    <Stack spacing={3}>
      <TenantIdentityHeader tenant={tenant} opsNotProvisioned={opsError === 'not-found'} />

      {opsError === 'generic' ? (
        <Alert severity="error">
          {t('tenantDetail.opsLoadError', 'Could not load operational settings.')}
        </Alert>
      ) : null}

      {ops ? <ReadinessAlert summary={ops.summary} /> : null}

      {ops ? <DisabledSwitchesSection summary={ops.summary} /> : null}

      {ops ? <WarehouseSection summary={ops.summary} warehouse={ops.warehouse} /> : null}

      {ops ? (
        <ActivitySection summary={ops.summary} lastRuns={ops.lastRuns} nextRuns={ops.nextRuns} />
      ) : null}

      {ops ? (
        <ProblemsSection problems={ops.problems} verification={ops.summary.verification} />
      ) : null}

      <TenantQueueSection tenantSlug={tenant.slug} onDetailChanged={onDetailChanged} />
    </Stack>
  );
}

function IngestionPanel({
  slug,
  detail,
  loading,
  loadError,
  onRefetch,
  onDetailChanged,
}: {
  slug: string;
  detail: PlatformTenantDetail | null;
  loading: boolean;
  loadError: LoadError;
  onRefetch: () => void;
  onDetailChanged: (detail: PlatformTenantDetail) => void;
}) {
  const { t } = useCopy();
  const [tenantToggling, setTenantToggling] = useState(false);
  const [togglingService, setTogglingService] = useState<string | null>(null);
  const [runningServices, setRunningServices] = useState<Set<string>>(() => new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);

  const handleToggleTenant = async (enabled: boolean) => {
    setActionError(null);
    setActionNotice(null);
    setTenantToggling(true);
    try {
      await setTenantEnabled(slug, enabled);
      onRefetch();
    } catch {
      setActionError(
        t('tenantDetail.actions.enableError', 'Could not update tenant status. Please try again.'),
      );
    } finally {
      setTenantToggling(false);
    }
  };

  const handleToggleService = async (jobType: string, enabled: boolean) => {
    setActionError(null);
    setActionNotice(null);
    setTogglingService(jobType);
    try {
      await setServiceEnabled(slug, jobType, enabled);
      onRefetch();
    } catch {
      setActionError(
        t('tenantDetail.services.enableError', 'Could not update {{service}}.', {
          service: jobTypeLabel(jobType, t),
        }),
      );
    } finally {
      setTogglingService(null);
    }
  };

  const handleRunNow = async (jobType: string) => {
    const serviceLabel = jobTypeLabel(jobType, t);
    setActionError(null);
    setActionNotice(null);
    setRunningServices((current) => new Set(current).add(jobType));
    try {
      const result = await runNow(slug, jobType);
      setActionNotice(
        result.alreadyInFlight
          ? t('runNow.alreadyRunning', '{{service}} is already running.', {
              service: serviceLabel,
            })
          : t(
              'runNow.enqueued',
              '{{service}} queued. It appears in the Queue within a few seconds.',
              { service: serviceLabel },
            ),
      );
      onRefetch();
    } catch (error: unknown) {
      const blockedBy = runNowBlockedBy(error);
      if (blockedBy !== null) {
        const reasons =
          blockedBy.length > 0
            ? blockedBy.map((reason) => runNowBlockedReason(reason, t))
            : [runNowBlockedReason('unknown', t)];
        setActionError(
          `${t('runNow.blockedTitle', '{{service}} cannot run:', {
            service: serviceLabel,
          })} ${reasons.join(' ')}`,
        );
      } else {
        setActionError(
          t('runNow.error', 'Could not start {{service}}.', { service: serviceLabel }),
        );
      }
    } finally {
      setRunningServices((current) => {
        const next = new Set(current);
        next.delete(jobType);
        return next;
      });
    }
  };

  if (loading) return <LoadingSpinner />;

  if (loadError === 'not-found') {
    return (
      <Alert severity="info">
        {t(
          'tenantDetail.identity.opsNotProvisioned',
          "Operational settings for this tenant aren't available yet — wsr-api hasn't mirrored it. It will appear on its next roster sync.",
        )}
      </Alert>
    );
  }

  if (loadError === 'generic') {
    return (
      <Alert severity="error">
        {t('tenantDetail.opsLoadError', 'Could not load operational settings.')}
      </Alert>
    );
  }

  if (!detail) return null;

  return (
    <>
      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}
      {actionNotice ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionNotice(null)}>
          {actionNotice}{' '}
          <Link component={RouterLink} to="/queue">
            {t('runNow.queueLink', 'Open the Queue')}
          </Link>
        </Alert>
      ) : null}

      <AppSwitch
        label={
          detail.summary.tenantEnabled
            ? t('tenantDetail.actions.enabledLabel', 'Tenant enabled')
            : t('tenantDetail.actions.disabledLabel', 'Tenant disabled')
        }
        checked={detail.summary.tenantEnabled}
        onChange={(checked) => void handleToggleTenant(checked)}
        disabled={tenantToggling}
      />

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          {!detail.summary.wstudioConfigured ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(
                'tenants.detail.wstudioNotConfigured',
                'WStudio credentials not configured for this tenant.',
              )}
            </Alert>
          ) : null}

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {t('tenants.detail.servicesTitle', 'Services')}
          </Typography>
          <Stack spacing={1} sx={{ mb: 3 }}>
            {detail.summary.services.map((service) => {
              const canRun = service.effective === true;
              const isRunning = runningServices.has(service.jobType);
              return (
                <Box
                  key={service.jobType}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <AppSwitch
                    label={jobTypeLabel(service.jobType, t)}
                    checked={service.enabled}
                    onChange={(checked) => void handleToggleService(service.jobType, checked)}
                    disabled={togglingService === service.jobType}
                    title={
                      service.jobType === 'SALES_RECONCILE'
                        ? t(
                            'jobTypeHelp.SALES_RECONCILE',
                            'Re-walks sales history to catch later edits. A run that updates nothing is a healthy run.',
                          )
                        : undefined
                    }
                  />
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    {/* WSRAPI-0079 — render the server's published outcome, never re-derive it.
                        `service.effective` is undefined pre-deploy; render no indicator at all
                        rather than a client-side guess (same precedent as WSRPDM-0041's
                        ReadinessAlert: a temporarily missing indicator beats a confidently
                        wrong one). */}
                    {service.effective !== undefined ? (
                      <Chip
                        size="small"
                        label={t('tenants.detail.effectiveLabel', 'Effective: {{state}}', {
                          state: service.effective
                            ? t('tenantDetail.gate.gateOn', 'On')
                            : t('tenantDetail.gate.gateOff', 'Off'),
                        })}
                        color={service.effective ? 'success' : 'default'}
                        variant={service.effective ? 'filled' : 'outlined'}
                      />
                    ) : null}
                    <Tooltip title={canRun ? '' : runNowTooltip(detail.summary, service, t)}>
                      <span>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!canRun || isRunning}
                          onClick={() => void handleRunNow(service.jobType)}
                        >
                          {t('runNow.button', 'Run now')}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2">
              {t('tenantDetail.gate.title', 'Enable switches')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t(
                'tenantDetail.gate.explainer',
                'A job runs only when global, tenant, and service are ALL on. If global or tenant is off, all services are paused even if their individual switches are on.',
              )}
            </Typography>
            <Stack direction="row" spacing={3}>
              <Typography variant="body2">
                {t('tenantDetail.gate.globalLevel', 'Global')}:{' '}
                {detail.summary.globalEnabled
                  ? t('tenantDetail.gate.gateOn', 'On')
                  : t('tenantDetail.gate.gateOff', 'Off')}
              </Typography>
              <Typography variant="body2">
                {t('tenantDetail.gate.tenantLevel', 'Tenant')}:{' '}
                {detail.summary.tenantEnabled
                  ? t('tenantDetail.gate.gateOn', 'On')
                  : t('tenantDetail.gate.gateOff', 'Off')}
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ConfigForm key={slug} slug={slug} config={detail.config} onSaved={onDetailChanged} />
        </Grid>
      </Grid>
    </>
  );
}

export function TenantDetailPage() {
  const { tenantId } = useParams();
  return <TenantDetailPageContent key={tenantId} tenantIdParam={tenantId} />;
}

function TenantDetailPageContent({ tenantIdParam }: { tenantIdParam: string | undefined }) {
  const { t } = useCopy();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = Number(tenantIdParam);
  const validTenantId = Number.isInteger(tenantId) && tenantId > 0;
  const requestedTab = searchParams.get('tab');
  const activeTab = resolveTab(requestedTab);
  const [tenant, setTenant] = useState<TenantDto | null>(null);
  const [identityLoading, setIdentityLoading] = useState(validTenantId);
  const [identityError, setIdentityError] = useState<LoadError>(validTenantId ? null : 'not-found');
  const [ops, setOps] = useState<PlatformTenantDetail | null>(null);
  const [opsLoading, setOpsLoading] = useState(true);
  const [opsError, setOpsError] = useState<LoadError>(null);
  const [opsReloadToken, setOpsReloadToken] = useState(0);
  const [invitationsRefreshKey, setInvitationsRefreshKey] = useState(0);

  useEffect(() => {
    if (searchParams.get('tab') !== 'settings') return;
    setSearchParams({ tab: 'wstudio' }, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!validTenantId) return;
    let cancelled = false;
    getIdentityTenant(tenantId)
      .then((identity) => {
        if (cancelled) return;
        setTenant(identity);
        setIdentityError(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setIdentityError(
          error instanceof ApiError && error.status === 404 ? 'not-found' : 'generic',
        );
      })
      .finally(() => {
        if (!cancelled) setIdentityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, validTenantId]);

  useEffect(() => {
    if (!tenant) return;
    let cancelled = false;
    getTenant(tenant.slug)
      .then((detail) => {
        if (cancelled) return;
        setOps(detail);
        setOpsError(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setOps(null);
        setOpsError(error instanceof ApiError && error.status === 404 ? 'not-found' : 'generic');
      })
      .finally(() => {
        if (!cancelled) setOpsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [opsReloadToken, tenant]);

  const refetchOps = useCallback(() => {
    setOpsLoading(true);
    setOpsError(null);
    setOpsReloadToken((token) => token + 1);
  }, []);

  const selectTab = (tab: TabKey) => {
    if (tab === 'overview') {
      setSearchParams({});
      return;
    }
    if (tab === 'history') {
      const job = searchParams.get('job');
      if (parseJobId(job) !== null && job) {
        setSearchParams({ tab: 'history', job });
        return;
      }
      setSearchParams({ tab: 'history' });
      return;
    }
    setSearchParams({ tab });
  };

  if (identityLoading) return <LoadingSpinner />;

  if (identityError === 'not-found') {
    return (
      <Box>
        <Link component={RouterLink} to="/tenants">
          {t('tenantDetail.backToRoster', 'Back to tenants')}
        </Link>
        <Typography variant="h1" component="h1" sx={{ mt: 2 }}>
          {t('tenantDetail.notFound', 'Tenant not found')}
        </Typography>
      </Box>
    );
  }

  if (identityError === 'generic' || !tenant) {
    return (
      <Box>
        <Link component={RouterLink} to="/tenants">
          {t('tenantDetail.backToRoster', 'Back to tenants')}
        </Link>
        <Alert severity="error" sx={{ mt: 2 }}>
          {t('tenantDetail.identity.loadError', 'Could not load tenant identity.')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Link component={RouterLink} to="/tenants">
        {t('tenantDetail.backToRoster', 'Back to tenants')}
      </Link>
      <Typography variant="h1" component="h1" sx={{ mt: 1 }}>
        {tenant.name}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {tenant.slug}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_event, value: TabKey) => selectTab(value)}
          aria-label={t('tenantDetail.tabs.ariaLabel', 'Tenant detail sections')}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TAB_KEYS.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              label={t(`tenant.tab.${tab}`, tab)}
              id={`tenant-tab-${tab}`}
              aria-controls={`tenant-panel-${tab}`}
            />
          ))}
        </Tabs>
      </Box>

      <Box
        id={`tenant-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`tenant-tab-${activeTab}`}
      >
        {activeTab === 'overview' ? (
          <OverviewPanel tenant={tenant} ops={ops} opsError={opsError} onDetailChanged={setOps} />
        ) : null}
        {activeTab === 'members' ? (
          <MembersPanel
            tenantId={tenant.id}
            onMemberInvited={() => setInvitationsRefreshKey((key) => key + 1)}
          />
        ) : null}
        {activeTab === 'invitations' ? (
          <InvitationsPanel tenantId={tenant.id} refreshKey={invitationsRefreshKey} />
        ) : null}
        {activeTab === 'ingestion' ? (
          <IngestionPanel
            slug={tenant.slug}
            detail={ops}
            loading={opsLoading}
            loadError={opsError}
            onRefetch={refetchOps}
            onDetailChanged={setOps}
          />
        ) : null}
        {activeTab === 'history' ? <SyncHistoryPanel slug={tenant.slug} /> : null}
        {isConfigTab(activeTab) ? (
          <WStudioSettings
            key={tenant.slug}
            slug={tenant.slug}
            tenant={tenant}
            summary={ops?.summary ?? null}
            onSummaryRefresh={refetchOps}
            section={activeTab}
          />
        ) : null}
      </Box>
    </Box>
  );
}
